import { Transaction } from "./types";

/**
 * Клиент живой ленты транзакций (WSS /transactions/ws).
 *
 * Контракт описан в README_TRANSACTIONS_WEBSOCKET.md на стороне бэкенда.
 * Здесь только транспорт: накопление событий до готовности списка, слежение
 * за живостью соединения и переподключение. Применение событий к таблице —
 * задача страницы.
 */

export type FeedTransaction = Partial<Transaction> & { id: string };

export type FeedEvent =
  | { type: "ready"; emitted_at: string }
  | { type: "ping" }
  | { type: "pong" }
  | { type: "lagged"; emitted_at?: string }
  | { type: "created"; emitted_at: string; transaction: FeedTransaction }
  | {
      type: "updated";
      emitted_at: string;
      changed: string[];
      previous: Record<string, unknown>;
      transaction: FeedTransaction;
    };

export type FeedStatus = "online" | "offline";

export type FeedHandlers = {
  /** Здесь запрашивается список, после чего вызывается drain(). */
  onReady: () => void;
  onEvent: (event: FeedEvent) => void;
  /** 4401 — кука протухла. */
  onAuthLost: () => void;
  /** 4403 — роль без доступа. */
  onForbidden: () => void;
  /** 4400 — origin не в списке доверенных, ошибка конфигурации бэкенда. */
  onRejected: (reason: string) => void;
  onStatus: (status: FeedStatus) => void;
};

/** Тишина дольше этого срока означает мёртвое соединение. */
const SILENCE_LIMIT_MS = 60000;

const MAX_BACKOFF_MS = 30000;

export class TransactionFeed {
  private ws: WebSocket | null = null;
  private attempt = 0;
  private buffer: FeedEvent[] = [];
  private buffering = true;
  private closedByUs = false;
  private aliveTimer?: number;
  private reconnectTimer?: number;

  constructor(
    private readonly url: string,
    private readonly handlers: FeedHandlers,
  ) {}

  connect() {
    this.closedByUs = false;
    this.buffering = true;
    this.buffer = [];

    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch (err) {
      // Некорректный адрес — повторять бессмысленно.
      this.handlers.onRejected(String(err));
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.attempt = 0;
      this.handlers.onStatus("online");
      this.armWatchdog();
    };

    ws.onmessage = (event) => {
      this.armWatchdog();

      let message: FeedEvent;
      try {
        message = JSON.parse(event.data);
      } catch {
        return;
      }

      if (message.type === "ping") {
        this.send({ type: "pong" });
        return;
      }
      if (message.type === "pong") return;

      if (message.type === "ready") {
        this.handlers.onReady();
        return;
      }

      if (this.buffering) this.buffer.push(message);
      else this.handlers.onEvent(message);
    };

    ws.onclose = (event) => {
      this.clearWatchdog();
      this.handlers.onStatus("offline");

      // Переподключение на этих кодах даёт бесконечный цикл отказов.
      if (event.code === 4401) return this.handlers.onAuthLost();
      if (event.code === 4403) return this.handlers.onForbidden();
      if (event.code === 4400) {
        return this.handlers.onRejected("origin не в списке доверенных");
      }

      if (!this.closedByUs) this.scheduleReconnect();
    };

    ws.onerror = () => ws.close();
  }

  /** Вызывается после загрузки списка: отдаёт события, накопленные за запрос. */
  drain() {
    this.buffering = false;
    const pending = this.buffer;
    this.buffer = [];
    pending.forEach((event) => this.handlers.onEvent(event));
  }

  close() {
    this.closedByUs = true;
    this.clearWatchdog();
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.ws?.close();
    this.ws = null;
  }

  private send(payload: unknown) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
    }
  }

  private scheduleReconnect() {
    // Разброс обязателен: без него после перезапуска бэкенда все открытые
    // вкладки ломятся обратно одновременно.
    const base = Math.min(1000 * 2 ** this.attempt, MAX_BACKOFF_MS);
    this.attempt += 1;
    const delay = base * (0.5 + Math.random() * 0.5);
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
  }

  /**
   * Повисшее TCP-соединение браузер сам не закрывает, onclose может не прийти
   * минутами. Без этого лента молча умирает, и пользователь не замечает.
   */
  private armWatchdog() {
    this.clearWatchdog();
    this.aliveTimer = window.setTimeout(
      () => this.ws?.close(),
      SILENCE_LIMIT_MS,
    );
  }

  private clearWatchdog() {
    if (this.aliveTimer) window.clearTimeout(this.aliveTimer);
  }
}
